import { AlertTriangle, Check, CircleAlert, Loader2 } from 'lucide-react';
import React, { createContext, useContext, useState, useCallback } from 'react';

interface SnackbarContextType {
  showSnackbar: (message: string, options?: SnackbarOptions) => void;
}

export interface SnackbarOptions {
  duration?: number;
  type?: 'success' | 'error' | 'info' | 'warning';
}

interface SnackbarProviderProps {
  children: React.ReactNode;
}

const SnackbarContext = createContext<SnackbarContextType | undefined>(
  undefined
);

function SnackbarLeadingIcon({ type }: { type: string }) {
  switch (type) {
    case 'success':
      return <Check className="h-4 w-4 shrink-0 text-green-500" aria-hidden strokeWidth={2.5} />;
    case 'error':
      return (
        <CircleAlert className="h-4 w-4 shrink-0 text-red-500" aria-hidden strokeWidth={2} />
      );
    case 'warning':
      return (
        <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500" aria-hidden strokeWidth={2} />
      );
    default:
      return (
        <Loader2
          className="h-4 w-4 shrink-0 animate-spin text-blue-500"
          aria-hidden
          strokeWidth={2}
        />
      );
  }
}

export const useSnackbar = () => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error('useSnackbar must be used within a SnackbarProvider');
  }
  return context;
};

const SnackbarProvider: React.FC<SnackbarProviderProps> = ({ children }) => {
  const [snackbar, setSnackbar] = useState<{
    message: string;
    visible: boolean;
    type: string;
  }>({
    message: '',
    visible: false,
    type: 'info',
  });

  const showSnackbar = useCallback(
    (message: string, options?: SnackbarOptions) => {
      setSnackbar({
        message,
        visible: true,
        type: options?.type || 'info',
      });

      setTimeout(() => {
        setSnackbar((prev) => ({ ...prev, visible: false }));
      }, options?.duration || 3000);
    },
    []
  );

  return (
    <SnackbarContext.Provider value={{ showSnackbar }}>
      {children}
      {snackbar.visible && (
        <div
          className="fixed left-1/2 z-50 -translate-x-1/2 px-3
            bottom-[calc(3.25rem+max(0.35rem,env(safe-area-inset-bottom,0px))+1rem)]
            lg:bottom-6"
        >
          <div
            className="flex max-w-[min(100vw-1.5rem,32rem)] items-center gap-2 rounded-md border
              border-gray-600/90 bg-gray-900/95 px-3 py-1.5 text-left text-xs font-medium
              leading-snug text-gray-100 shadow-lg backdrop-blur-sm"
          >
            <SnackbarLeadingIcon type={snackbar.type} />
            <span className="min-w-0 flex-1">{snackbar.message}</span>
          </div>
        </div>
      )}
    </SnackbarContext.Provider>
  );
};

export default SnackbarProvider;
